import {
  Divider,
  NavCategory,
  NavCategoryItem,
  NavDrawer,
  NavDrawerBody,
  NavItem,
  NavSubItem,
  NavSubItemGroup,
} from '@fluentui/react-components';
import { Fragment } from 'react';
import { toRoutePath } from '@unified-governance/shared';
import type { NavNode } from '@unified-governance/shared';
import type { MenuSection } from '../shell.types';
import { getIcon } from './iconRegistry';

interface NavMenuProps {
  sections: MenuSection[];
  selectedPath: string;
  onNavigate: (routePath: string) => void;
}

export const NavMenu = ({ sections, selectedPath, onNavigate }: NavMenuProps) => {
  const renderNode = (
    basePath: string,
    node: NavNode,
    itemIconDataUrls: Record<string, string>,
    groupIconDataUrls: Record<string, string>
  ) => {
    if (node.kind === 'divider') {
      return <Divider key={`${basePath}-${node.id}`} />;
    }

    if (node.kind === 'group') {
      return (
        <NavCategory key={`${basePath}-${node.id}`} value={`${basePath}-${node.id}`}>
          <NavCategoryItem icon={getIcon(node.icon, groupIconDataUrls[node.id])}>
            {node.label}
          </NavCategoryItem>
          <NavSubItemGroup>
            {node.children.map((child) => {
              const route = toRoutePath(basePath, child.path);
              return (
                <NavSubItem key={route} value={route} onClick={() => onNavigate(route)}>
                  {child.label}
                </NavSubItem>
              );
            })}
          </NavSubItemGroup>
        </NavCategory>
      );
    }

    const route = toRoutePath(basePath, node.path);
    return (
      <NavItem
        key={route}
        value={route}
        icon={getIcon(node.icon, itemIconDataUrls[node.path])}
        onClick={() => onNavigate(route)}
      >
        {node.label}
      </NavItem>
    );
  };

  return (
    <NavDrawer aria-label="Primary navigation" selectedValue={selectedPath} open type="inline">
      <NavDrawerBody>
        <NavItem value="/" icon={getIcon('home')} onClick={() => onNavigate('/')}>
          Home
        </NavItem>
        {sections
          .filter((section) => section.items.length > 0)
          .map((section, index) => (
            <Fragment key={section.remoteName}>
              <Divider className={index === 0 ? 'shell-nav-section-divider-first' : undefined} />
              <div className="shell-nav-section-label">{section.appName}</div>
              {section.items.map((node) =>
                renderNode(
                  section.basePath,
                  node,
                  section.itemIconDataUrls,
                  section.groupIconDataUrls
                )
              )}
            </Fragment>
          ))}
      </NavDrawerBody>
    </NavDrawer>
  );
};
